import type { Screen } from '../ui/flow'

/** `online`: a server race with a Supabase user; `offline`: guest without Supabase. */
export type ErrorMode = 'online' | 'offline' | 'practice'

/** What every error event is tagged with, read when the event is sent. */
export interface ErrorContext {
  screen: Screen
  mode: ErrorMode
  /** Supabase user id (allowed in events, D-041); null offline and in practice. */
  userId: string | null
}

export interface ErrorReportingOptions {
  dsn: string | undefined
  /** `VITE_RELEASE`: the commit's short sha on Cloudflare Pages, else `dev`. */
  release: string
  environment: string
  context: () => ErrorContext
}

/** The lazily loaded Sentry module (`./sentry`), injectable for tests. */
export type SentryLoader = () => Promise<{
  initSentry: (opts: ErrorReportingOptions & { dsn: string }) => void
}>

const loadSentry: SentryLoader = () => import('./sentry')

/**
 * Start error reporting (D-041): with `VITE_SENTRY_DSN` empty nothing is loaded (one console
 * line); otherwise `@sentry/browser` comes in its own chunk, after the first screen rendered, so
 * the initial JS stays small. Errors thrown before it arrives are not reported. Returns whether
 * Sentry is being loaded.
 */
export function startErrorReporting(opts: ErrorReportingOptions, load = loadSentry): boolean {
  const { dsn } = opts
  if (!dsn) {
    console.info('[errors] Sentry off (VITE_SENTRY_DSN empty)')
    return false
  }
  void load()
    .then((m) => m.initSentry({ ...opts, dsn }))
    .catch((err: unknown) => console.warn('[errors] Sentry failed to load', err))
  return true
}
